import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Lock, Mail, Calendar, Trash2, Download, RefreshCw, 
  MessageSquare, LogOut 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [demoRequests, setDemoRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('contacts');
  
  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: null,
    id: null,
    name: ''
  });

  // Check session on mount
  useEffect(() => {
    const session = sessionStorage.getItem('vsp-admin-auth');
    if (session === 'authenticated') {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        sessionStorage.setItem('vsp-admin-auth', 'authenticated');
        setIsAuthenticated(true);
        toast.success('Welcome to VSP Admin');
      }
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('vsp-admin-auth');
    setIsAuthenticated(false);
    setPassword('');
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contactsRes, demosRes] = await Promise.all([
        axios.get(`${API}/contact`),
        axios.get(`${API}/demo-request`),
      ]);
      setContacts(contactsRes.data);
      setDemoRequests(demosRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (type, id, name) => {
    setDeleteDialog({ open: true, type, id, name });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, type: null, id: null, name: '' });
  };

  const confirmDelete = async () => {
    const { type, id } = deleteDialog;
    try {
      await axios.delete(`${API}/${type}/${id}`);
      toast.success('Deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      closeDeleteDialog();
    }
  };

  const exportToCSV = (data, filename) => {
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0]).filter(k => k !== 'id');
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(h => {
          const val = row[h] || '';
          const escaped = String(val).replace(/"/g, '""');
          return escaped.includes(',') ? `"${escaped}"` : escaped;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV exported successfully');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#1D1D1F] rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-semibold text-[#1D1D1F] font-['Plus_Jakarta_Sans']">
                VSP Admin
              </h1>
              <p className="text-[#86868B] mt-2">Enter password to access dashboard</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="password" className="text-[#1D1D1F] mb-2 block">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl border-[#D2D2D7] focus:ring-[#007AFF]"
                  placeholder="Enter admin password"
                  required
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loginLoading}
                className="w-full bg-[#1D1D1F] text-white hover:bg-black rounded-full py-3 text-base font-medium"
                data-testid="admin-login-btn"
              >
                {loginLoading ? 'Verifying...' : 'Access Dashboard'}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="admin-dashboard">
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && closeDeleteDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteDialog.type === 'contact' ? 'Contact Message' : 'Demo Request'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {deleteDialog.type === 'contact' ? 'message' : 'request'} from {deleteDialog.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeDeleteDialog}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_ced1def1-5c79-41a6-a911-205351f73e31/artifacts/90ux9tsg_v...%202.PNG"
              alt="VSP"
              className="h-8 w-8"
            />
            <span className="text-xl font-semibold text-[#1D1D1F] font-['Plus_Jakarta_Sans']">
              VSP Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={fetchData}
              variant="outline"
              className="rounded-full"
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#007AFF15] rounded-xl flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-[#007AFF]" />
              </div>
              <div>
                <p className="text-sm text-[#86868B]">Contact Messages</p>
                <p className="text-3xl font-semibold text-[#1D1D1F]">{contacts.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#34C75915] rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[#34C759]" />
              </div>
              <div>
                <p className="text-sm text-[#86868B]">Demo Requests</p>
                <p className="text-3xl font-semibold text-[#1D1D1F]">{demoRequests.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList className="bg-white rounded-full p-1 border border-slate-200">
              <TabsTrigger
                value="contacts"
                className="rounded-full px-6 data-[state=active]:bg-[#1D1D1F] data-[state=active]:text-white"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Contacts
              </TabsTrigger>
              <TabsTrigger
                value="demos"
                className="rounded-full px-6 data-[state=active]:bg-[#1D1D1F] data-[state=active]:text-white"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Demo Requests
              </TabsTrigger>
            </TabsList>

            <Button
              onClick={() => exportToCSV(
                activeTab === 'contacts' ? contacts : demoRequests,
                activeTab === 'contacts' ? 'vsp_contacts' : 'vsp_demo_requests'
              )}
              variant="outline"
              className="rounded-full"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {/* Contacts Table */}
          <TabsContent value="contacts">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              {contacts.length === 0 ? (
                <div className="text-center py-16">
                  <MessageSquare className="w-12 h-12 text-[#D2D2D7] mx-auto mb-4" />
                  <p className="text-[#86868B]">No contact messages yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5F7]">
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Company</TableHead>
                      <TableHead className="font-semibold">Message</TableHead>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.map((contact) => (
                      <TableRow key={contact.id} className="hover:bg-[#F5F5F7]/50">
                        <TableCell className="font-medium">{contact.name}</TableCell>
                        <TableCell>
                          <a href={`mailto:${contact.email}`} className="text-[#007AFF] hover:underline">
                            {contact.email}
                          </a>
                        </TableCell>
                        <TableCell>{contact.company || '-'}</TableCell>
                        <TableCell className="max-w-[300px] truncate">{contact.message}</TableCell>
                        <TableCell className="text-[#86868B] text-sm">
                          {formatDate(contact.created_at)}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => openDeleteDialog('contact', contact.id, contact.name)}
                            data-testid={`delete-contact-${contact.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          {/* Demo Requests Table */}
          <TabsContent value="demos">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              {demoRequests.length === 0 ? (
                <div className="text-center py-16">
                  <Calendar className="w-12 h-12 text-[#D2D2D7] mx-auto mb-4" />
                  <p className="text-[#86868B]">No demo requests yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F5F7]">
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Company</TableHead>
                      <TableHead className="font-semibold">Preferred Date</TableHead>
                      <TableHead className="font-semibold">Preferred Time</TableHead>
                      <TableHead className="font-semibold">Submitted</TableHead>
                      <TableHead className="font-semibold w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {demoRequests.map((demo) => (
                      <TableRow key={demo.id} className="hover:bg-[#F5F5F7]/50">
                        <TableCell className="font-medium">{demo.name}</TableCell>
                        <TableCell>
                          <a href={`mailto:${demo.email}`} className="text-[#007AFF] hover:underline">
                            {demo.email}
                          </a>
                        </TableCell>
                        <TableCell>{demo.company || '-'}</TableCell>
                        <TableCell>{demo.date || '-'}</TableCell>
                        <TableCell>{demo.time || '-'}</TableCell>
                        <TableCell className="text-[#86868B] text-sm">
                          {formatDate(demo.created_at)}
                        </TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => openDeleteDialog('demo-request', demo.id, demo.name)}
                            data-testid={`delete-demo-${demo.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
